import { Trans, useLingui } from "@lingui/react/macro";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { Button } from "@offstreetparking/design-system/forms";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Link, Text } from "@offstreetparking/design-system/typography";
import { routes } from "../../../../routes";
import { FlowModal as Modal } from "../../../overlays/FlowModal/FlowModal";
import { FlowModalTitle as ModalTitle } from "../../../overlays/FlowModal/FlowModalTitle";
import type { PermitFormData } from "../../PermitContextProvider";
type ExistingPermitModalProps = {
	plate: string;
	permitIntervals: PermitFormData["permitIntervals"];
	timezone: string;
	publicIdentifier: string;
	onClose: () => void;
	locationContact?: {
		email?: string | undefined;
		formattedPhoneNumber?: string | undefined;
	};
};
export const ExistingPermitModal = ({
	plate,
	permitIntervals,
	timezone,
	publicIdentifier,
	onClose,
	locationContact,
}: ExistingPermitModalProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	return (
		<Modal
			i18n={{
				closeButton: t`Close`,
				cancelButton: t`Cancel`,
			}}
			isOpen={true}
			onClose={onClose}
			actions={
				<>
					<Button
						intent="primary"
						href={routes.permitConfirmation({
							publicIdentifier,
						})}
					>
						{t`View Permit`}
					</Button>
					<Button intent="secondary" type="button" onClick={onClose}>
						{t`Cancel`}
					</Button>
				</>
			}
		>
			<ModalTitle>{t`You're already registered!`}</ModalTitle>

			<div className="space-y-4">
				{(permitIntervals ?? []).map((interval) => (
					<Text key={interval.startDateTimeISO}>
						<Trans>
							<b>{plate}</b> is already registered from{" "}
							<b>
								{formatDate({
									date: interval.startDateTimeISO,
									formatType: "Abbreviated_DateTime",
									timezone: timezone,
									language: locale,
								})}
							</b>{" "}
							to{" "}
							<b>
								{interval.endDateTimeISO
									? formatDate({
											date: interval.endDateTimeISO,
											formatType: "Abbreviated_DateTime",
											timezone: timezone,
											language: locale,
										})
									: ""}
							</b>
						</Trans>
					</Text>
				))}
				<Text>
					{locationContact?.email && locationContact?.formattedPhoneNumber ? (
						<Trans comment="Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.">
							<span>To change session details, contact </span>
							<Link href={`mailto:${locationContact.email}`}>{locationContact.email}</Link>
							<span> or </span>
							<Link href={`tel:${locationContact.formattedPhoneNumber}`}>
								{locationContact.formattedPhoneNumber}
							</Link>
						</Trans>
					) : locationContact?.email ? (
						<Trans comment="Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.">
							<span>To change session details, contact </span>
							<Link href={`mailto:${locationContact.email}`}>{locationContact.email}</Link>
						</Trans>
					) : locationContact?.formattedPhoneNumber ? (
						<Trans comment="Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.">
							<span>To change session details, contact </span>
							<Link href={`tel:${locationContact.formattedPhoneNumber}`}>
								{locationContact.formattedPhoneNumber}
							</Link>
						</Trans>
					) : (
						t({
							message: "To change session details, contact your administrator",
							comment:
								"Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.",
						})
					)}
				</Text>
			</div>
		</Modal>
	);
};
